import { ref, onMounted, onUnmounted, watch, type Ref } from "vue"

export function useOverlay(
  isVisible: Ref<boolean>,
  options: {
    closeOnEscape?: boolean
    closeOnClickOutside?: boolean
    onClose?: () => void
  } = {}
) {
  const overlayRef = ref<HTMLElement | null>(null)
  const contentRef = ref<HTMLElement | null>(null)
  const lastActiveElement = ref<HTMLElement | null>(null)

  const toggleBodyScroll = (disable: boolean) => {
    document.body.style.overflow = disable ? "hidden" : ""
  }

  const close = () => {
    options.onClose?.()
  }

  const handleKeydown = (e: KeyboardEvent) => {
    if (options.closeOnEscape !== false && e.key === "Escape" && isVisible.value) {
      e.preventDefault()
      close()
    }
  }

  const handleClickOutside = (e: MouseEvent) => {
    if (
      options.closeOnClickOutside !== false &&
      overlayRef.value &&
      contentRef.value &&
      !contentRef.value.contains(e.target as Node)
    ) {
      close()
    }
  }

  const trapFocus = (e: KeyboardEvent) => {
    if (!isVisible.value || !contentRef.value) return
    if (e.key !== "Tab") return

    const focusableElements = contentRef.value.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )

    const firstFocusable = focusableElements[0]
    const lastFocusable = focusableElements[focusableElements.length - 1]

    if (e.shiftKey && document.activeElement === firstFocusable) {
      lastFocusable?.focus()
      e.preventDefault()
    } else if (!e.shiftKey && document.activeElement === lastFocusable) {
      firstFocusable?.focus()
      e.preventDefault()
    }
  }

  onMounted(() => {
    if (options.closeOnEscape !== false) {
      document.addEventListener("keydown", handleKeydown)
    }
    document.addEventListener("keydown", trapFocus)
  })

  onUnmounted(() => {
    document.removeEventListener("keydown", handleKeydown)
    document.removeEventListener("keydown", trapFocus)
    toggleBodyScroll(false)
  })

  watch(isVisible, (newValue) => {
    toggleBodyScroll(newValue)

    if (newValue) {
      lastActiveElement.value = document.activeElement as HTMLElement
      setTimeout(() => {
        if (contentRef.value) {
          const firstFocusable = contentRef.value.querySelector<HTMLElement>(
            'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
          )
          firstFocusable?.focus()
        }
      }, 50)
    } else {
      lastActiveElement.value?.focus()
    }
  })

  return { overlayRef, contentRef, handleClickOutside, close }
}
